import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  Select,
  Button,
  Table,
  Tag,
  message,
  Spin,
  Space,
  Checkbox,
  Tooltip,
  Modal,
  Descriptions,
  Badge,
  Progress,
  Alert
} from 'antd';
import {
  SearchOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
  DatabaseOutlined,
  CloudDownloadOutlined,
  StopOutlined,
  UndoOutlined
} from '@ant-design/icons';
import {
  fetchRACityListings,
  compareRAEvents,
  scrapeRAEvent,
  getSkippedRAEvents,
  skipRAEvent,
  bulkSkipRAEvents,
  unskipRAEvent,
  RA_CITIES,
  RA_COUNTRIES
} from '../../server/scraper/raScraperServer';

const { Option } = Select;

// Mapping from RA country codes to database country names
const RA_TO_DB_COUNTRY = {
  'ca': 'Canada',
  'us': 'United States',
  'uk': 'United Kingdom',
  'de': 'Germany',
  'es': 'Spain',
  'nl': 'Netherlands',
  'fr': 'France',
};

// Mapping from RA city slugs to database city names
const RA_TO_DB_CITY = {
  'montreal': 'Montreal',
  'toronto': 'Toronto',
  'vancouver': 'Vancouver',
  'calgary': 'Calgary',
  'ottawa': 'Ottawa',
  'newyork': 'New York',
  'losangeles': 'Los Angeles',
  'chicago': 'Chicago',
  'miami': 'Miami',
  'sanfrancisco': 'San Francisco',
  'detroit': 'Detroit',
  'seattle': 'Seattle',
  'london': 'London',
  'manchester': 'Manchester',
  'bristol': 'Bristol',
  'glasgow': 'Glasgow',
  'leeds': 'Leeds',
  'berlin': 'Berlin',
  'munich': 'Munich',
  'frankfurt': 'Frankfurt',
  'barcelona': 'Barcelona',
  'madrid': 'Madrid',
  'ibiza': 'Ibiza',
  'amsterdam': 'Amsterdam',
  'paris': 'Paris',
};

const RAComparatorTab = ({ onScrapeEvent, countries = [] }) => {
  // State for filters
  const [selectedCountry, setSelectedCountry] = useState('ca');
  const [selectedCity, setSelectedCity] = useState('montreal');

  // State for data
  const [loading, setLoading] = useState(false);
  const [comparing, setComparing] = useState(false);
  const [raEvents, setRaEvents] = useState([]);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [selectedEvents, setSelectedEvents] = useState([]);

  // State for scraping
  const [scrapingEvent, setScrapingEvent] = useState(null);
  const [scrapingProgress, setScrapingProgress] = useState({ current: 0, total: 0 });
  const [batchScraping, setBatchScraping] = useState(false);

  // State for event details modal
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [eventDetails, setEventDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // State for skipped events
  const [skippedEventIds, setSkippedEventIds] = useState([]);
  const [skippingEvent, setSkippingEvent] = useState(null);
  const [bulkSkipping, setBulkSkipping] = useState(false);

  // Get cities based on selected country
  const availableCities = RA_CITIES[selectedCountry] || [];

  // Fetch skipped events on mount
  useEffect(() => {
    const fetchSkipped = async () => {
      try {
        const result = await getSkippedRAEvents(1, 1000);
        const ids = (result.data?.data || []).map(e => e.raEventId);
        setSkippedEventIds(ids);
      } catch (error) {
        console.error('Failed to fetch skipped events:', error);
      }
    };
    fetchSkipped();
  }, []);

  // Handle country change
  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    const cities = RA_CITIES[country] || [];
    setSelectedCity(cities[0]?.value || '');
    setRaEvents([]);
    setComparisonResult(null);
    setSelectedEvents([]);
  };

  // Fetch RA listings
  const handleFetchListings = async () => {
    if (!selectedCity) {
      message.warning('Please select a city');
      return;
    }

    try {
      setLoading(true);
      setComparisonResult(null);
      setSelectedEvents([]);

      const result = await fetchRACityListings({
        city: selectedCity,
        country: selectedCountry
      });

      setRaEvents(result.events || []);
      message.success(`Fetched ${result.totalEvents || 0} events from RA.co`);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch RA listings';
      message.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Compare with database
  const handleCompare = async () => {
    if (raEvents.length === 0) {
      message.warning('No events to compare. Fetch RA listings first.');
      return;
    }

    try {
      setComparing(true);

      const result = await compareRAEvents(raEvents);
      setComparisonResult(result.data);

      // Auto-select missing events
      const missingIds = (result.data?.missingEvents || []).map(e => e.raEventId);
      setSelectedEvents(missingIds);

      message.success(`Comparison complete: ${result.data?.missing || 0} missing events`);
    } catch (error) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to compare events';
      message.error(errorMsg);
    } finally {
      setComparing(false);
    }
  };

  // View event details
  const handleViewDetails = async (event) => {
    try {
      setLoadingDetails(true);
      setDetailsModalVisible(true);

      const details = await scrapeRAEvent(event.url);
      setEventDetails(details);
    } catch (error) {
      message.error('Failed to fetch event details');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Get database country and city IDs from RA selection
  const getDbLocationIds = () => {
    const dbCountryName = RA_TO_DB_COUNTRY[selectedCountry];
    const dbCityName = RA_TO_DB_CITY[selectedCity];

    let countryId = null;
    let cityId = null;

    if (dbCountryName && countries.length > 0) {
      const foundCountry = countries.find(c =>
        c.name?.toLowerCase() === dbCountryName.toLowerCase()
      );

      if (foundCountry) {
        countryId = foundCountry._id;

        if (dbCityName && foundCountry.cities) {
          const foundCity = foundCountry.cities.find(city =>
            city.name?.toLowerCase() === dbCityName.toLowerCase()
          );
          if (foundCity) {
            cityId = foundCity._id;
          }
        }
      }
    }

    return { countryId, cityId };
  };

  // Scrape single event
  const handleScrapeEvent = async (event) => {
    try {
      setScrapingEvent(event.raEventId);

      if (onScrapeEvent) {
        // Pass city info and raEventId to the scrape handler
        const cityInfo = getDbLocationIds();
        await onScrapeEvent(event.url, {
          ...cityInfo,
          raEventId: event.raEventId
        });
        message.success(`Event "${event.title}" ready for creation`);
      } else {
        // Open in new tab if no callback
        window.open(event.url, '_blank');
      }
    } catch (error) {
      message.error('Failed to scrape event');
    } finally {
      setScrapingEvent(null);
    }
  };

  // Batch scrape selected events
  const handleBatchScrape = async () => {
    if (selectedEvents.length === 0) {
      message.warning('No events selected');
      return;
    }

    const eventsToScrape = comparisonResult?.missingEvents?.filter(
      e => selectedEvents.includes(e.raEventId)
    ) || [];

    if (eventsToScrape.length === 0) {
      message.warning('No missing events selected');
      return;
    }

    setBatchScraping(true);
    setScrapingProgress({ current: 0, total: eventsToScrape.length });

    for (let i = 0; i < eventsToScrape.length; i++) {
      const event = eventsToScrape[i];
      setScrapingProgress({ current: i + 1, total: eventsToScrape.length });

      try {
        if (onScrapeEvent) {
          await onScrapeEvent(event.url);
        }
        // Add delay to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 2000));
      } catch (error) {
        message.error(`Failed to scrape: ${event.title}`);
      }
    }

    setBatchScraping(false);
    message.success(`Batch scrape complete: ${eventsToScrape.length} events processed`);
  };

  // Skip single event
  const handleSkipEvent = async (event) => {
    try {
      setSkippingEvent(event.raEventId);
      await skipRAEvent({ raEventId: event.raEventId, title: event.title });
      setSkippedEventIds([...skippedEventIds, event.raEventId]);
      setSelectedEvents(selectedEvents.filter(id => id !== event.raEventId));
      message.success(`Event "${event.title}" skipped`);
    } catch (error) {
      message.error('Failed to skip event');
    } finally {
      setSkippingEvent(null);
    }
  };

  // Unskip single event
  const handleUnskipEvent = async (event) => {
    try {
      setSkippingEvent(event.raEventId);
      await unskipRAEvent(event.raEventId);
      setSkippedEventIds(skippedEventIds.filter(id => id !== event.raEventId));
      message.success(`Event "${event.title}" restored`);
    } catch (error) {
      message.error('Failed to unskip event');
    } finally {
      setSkippingEvent(null);
    }
  };

  // Bulk skip selected events
  const handleBulkSkip = async () => {
    if (selectedEvents.length === 0) {
      message.warning('No events selected');
      return;
    }

    const eventsToSkip = raEvents.filter(
      e => selectedEvents.includes(e.raEventId) && !skippedEventIds.includes(e.raEventId)
    ).map(e => ({ raEventId: e.raEventId, title: e.title }));

    if (eventsToSkip.length === 0) {
      message.warning('No events to skip');
      return;
    }

    try {
      setBulkSkipping(true);
      await bulkSkipRAEvents(eventsToSkip);
      const newSkippedIds = eventsToSkip.map(e => e.raEventId);
      setSkippedEventIds([...skippedEventIds, ...newSkippedIds]);
      setSelectedEvents([]);
      message.success(`${eventsToSkip.length} events skipped`);
    } catch (error) {
      message.error('Failed to skip events');
    } finally {
      setBulkSkipping(false);
    }
  };

  // Table columns for RA events
  const columns = [
    {
      title: (
        <Checkbox
          checked={selectedEvents.length === (comparisonResult?.missingEvents?.length || 0) && selectedEvents.length > 0}
          indeterminate={selectedEvents.length > 0 && selectedEvents.length < (comparisonResult?.missingEvents?.length || 0)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedEvents((comparisonResult?.missingEvents || []).map(e => e.raEventId));
            } else {
              setSelectedEvents([]);
            }
          }}
          disabled={!comparisonResult}
        />
      ),
      dataIndex: 'select',
      key: 'select',
      width: 50,
      render: (_, record) => {
        const isSkipped = skippedEventIds.includes(record.raEventId);
        const isMissing = comparisonResult?.missingEvents?.some(e => e.raEventId === record.raEventId);
        const canSelect = isMissing && !isSkipped;
        return (
          <Checkbox
            checked={selectedEvents.includes(record.raEventId)}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedEvents([...selectedEvents, record.raEventId]);
              } else {
                setSelectedEvents(selectedEvents.filter(id => id !== record.raEventId));
              }
            }}
            disabled={!canSelect}
          />
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (_, record) => {
        const isSkipped = skippedEventIds.includes(record.raEventId);
        if (isSkipped) {
          return <Tag icon={<StopOutlined />} color="default">Skipped</Tag>;
        }
        if (!comparisonResult) {
          return <Tag color="default">Unknown</Tag>;
        }
        const isInDB = comparisonResult.existingEvents?.some(e => e.raEventId === record.raEventId);
        return isInDB ? (
          <Tag icon={<CheckCircleOutlined />} color="success">In DB</Tag>
        ) : (
          <Tag icon={<ExclamationCircleOutlined />} color="warning">Missing</Tag>
        );
      }
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (text, record) => (
        <Tooltip title={text}>
          <a onClick={() => handleViewDetails(record)} style={{ cursor: 'pointer' }}>
            {text}
          </a>
        </Tooltip>
      )
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
    },
    {
      title: 'RA ID',
      dataIndex: 'raEventId',
      key: 'raEventId',
      width: 100,
      render: (id) => <code>{id}</code>
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (_, record) => {
        const isSkipped = skippedEventIds.includes(record.raEventId);
        const isInDB = comparisonResult?.existingEvents?.some(e => e.raEventId === record.raEventId);
        const isMissing = !comparisonResult || comparisonResult.missingEvents?.some(e => e.raEventId === record.raEventId);
        const isLoading = skippingEvent === record.raEventId;

        return (
          <Space>
            <Tooltip title="View on RA">
              <Button
                icon={<LinkOutlined />}
                size="small"
                onClick={() => window.open(record.url, '_blank')}
              />
            </Tooltip>
            {isSkipped ? (
              <Tooltip title="Restore event">
                <Button
                  icon={<UndoOutlined />}
                  size="small"
                  loading={isLoading}
                  onClick={() => handleUnskipEvent(record)}
                >
                  Unskip
                </Button>
              </Tooltip>
            ) : (
              <>
                <Tooltip title="Scrape & Add">
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="small"
                    loading={scrapingEvent === record.raEventId}
                    disabled={!isMissing || isInDB}
                    onClick={() => handleScrapeEvent(record)}
                  >
                    Add
                  </Button>
                </Tooltip>
                {isMissing && !isInDB && (
                  <Tooltip title="Skip this event">
                    <Button
                      icon={<StopOutlined />}
                      size="small"
                      loading={isLoading}
                      onClick={() => handleSkipEvent(record)}
                      danger
                    >
                      Skip
                    </Button>
                  </Tooltip>
                )}
              </>
            )}
          </Space>
        );
      }
    },
  ];

  return (
    <div className="space-y-6">
      {/* Filters Card */}
      <Card
        title={
          <div className="flex items-center gap-2">
            <SearchOutlined className="text-blue-500" />
            <span>RA Events Comparator</span>
          </div>
        }
        className="shadow-lg"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Country Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <Select
              value={selectedCountry}
              onChange={handleCountryChange}
              style={{ width: '100%' }}
              size="large"
            >
              {RA_COUNTRIES.map(country => (
                <Option key={country.value} value={country.value}>
                  {country.label}
                </Option>
              ))}
            </Select>
          </div>

          {/* City Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <Select
              value={selectedCity}
              onChange={setSelectedCity}
              style={{ width: '100%' }}
              size="large"
              placeholder="Select a city"
            >
              {availableCities.map(city => (
                <Option key={city.value} value={city.value}>
                  {city.label}
                </Option>
              ))}
            </Select>
          </div>

          {/* Actions */}
          <div className="flex items-end gap-2">
            <Button
              type="primary"
              icon={<CloudDownloadOutlined />}
              size="large"
              loading={loading}
              onClick={handleFetchListings}
              className="bg-blue-500"
            >
              Fetch RA
            </Button>
            <Button
              icon={<SyncOutlined />}
              size="large"
              loading={comparing}
              onClick={handleCompare}
              disabled={raEvents.length === 0}
            >
              Compare
            </Button>
          </div>
        </div>

        {/* API Preview */}
        <div className="text-sm text-gray-500">
          API: <code className="bg-gray-100 px-2 py-1 rounded">
            POST /batch/ra-city-listings {`{"city":"${selectedCity}","country":"${selectedCountry}"}`}
          </code>
        </div>
      </Card>

      {/* Stats Card */}
      {comparisonResult && (
        <Card className="shadow-lg">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-3xl font-bold text-blue-600">{comparisonResult.totalFromRA || 0}</div>
              <div className="text-sm text-gray-600">Total from RA</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-3xl font-bold text-green-600">{comparisonResult.inDatabase || 0}</div>
              <div className="text-sm text-gray-600">Already in DB</div>
            </div>
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <div className="text-3xl font-bold text-orange-600">{comparisonResult.missing || 0}</div>
              <div className="text-sm text-gray-600">Missing</div>
            </div>
            <div className="text-center p-4 bg-gray-100 rounded-lg">
              <div className="text-3xl font-bold text-gray-600">{comparisonResult.skippedCount || raEvents.filter(e => skippedEventIds.includes(e.raEventId)).length}</div>
              <div className="text-sm text-gray-600">Skipped</div>
            </div>
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <div className="text-3xl font-bold text-purple-600">{selectedEvents.length}</div>
              <div className="text-sm text-gray-600">Selected</div>
            </div>
          </div>
        </Card>
      )}

      {/* Batch Actions */}
      {comparisonResult && comparisonResult.missing > 0 && (
        <Card className="shadow-lg">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <span className="text-sm text-gray-600">
                {selectedEvents.length} events selected
              </span>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => setSelectedEvents([])}
                disabled={selectedEvents.length === 0}
              >
                Clear Selection
              </Button>
              <Button
                onClick={() => {
                  const missingNotSkipped = (comparisonResult?.missingEvents || [])
                    .filter(e => !skippedEventIds.includes(e.raEventId))
                    .map(e => e.raEventId);
                  setSelectedEvents(missingNotSkipped);
                }}
              >
                Select All Missing
              </Button>
              <Button
                icon={<StopOutlined />}
                loading={bulkSkipping}
                disabled={selectedEvents.length === 0}
                onClick={handleBulkSkip}
              >
                Skip Selected ({selectedEvents.length})
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                loading={batchScraping}
                disabled={selectedEvents.length === 0}
                onClick={handleBatchScrape}
              >
                Scrape Selected ({selectedEvents.length})
              </Button>
            </div>
          </div>

          {/* Batch Progress */}
          {batchScraping && (
            <div className="mt-4">
              <Progress
                percent={Math.round((scrapingProgress.current / scrapingProgress.total) * 100)}
                status="active"
                format={() => `${scrapingProgress.current}/${scrapingProgress.total}`}
              />
            </div>
          )}
        </Card>
      )}

      {/* Events Table */}
      <Card
        title={
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <DatabaseOutlined className="text-green-500" />
              <span>RA Events ({raEvents.length})</span>
            </div>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleFetchListings}
              loading={loading}
              size="small"
            >
              Refresh
            </Button>
          </div>
        }
        className="shadow-lg"
      >
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Spin size="large" />
          </div>
        ) : raEvents.length === 0 ? (
          <Alert
            message="No Events"
            description="Click 'Fetch RA' to load events from RA.co"
            type="info"
            showIcon
          />
        ) : (
          <Table
            columns={columns}
            dataSource={raEvents}
            rowKey="raEventId"
            pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (total) => `${total} events` }}
            size="middle"
            rowClassName={(record) => {
              const isSkipped = skippedEventIds.includes(record.raEventId);
              if (isSkipped) return 'bg-gray-100';
              if (!comparisonResult) return '';
              const isMissing = comparisonResult.missingEvents?.some(e => e.raEventId === record.raEventId);
              return isMissing ? 'bg-orange-50' : 'bg-green-50';
            }}
          />
        )}
      </Card>

      {/* Event Details Modal */}
      <Modal
        title="Event Details"
        open={detailsModalVisible}
        onCancel={() => {
          setDetailsModalVisible(false);
          setEventDetails(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="scrape"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              if (eventDetails) {
                handleScrapeEvent({ url: eventDetails.url || eventDetails.externalLink, raEventId: eventDetails.raEventId });
                setDetailsModalVisible(false);
              }
            }}
          >
            Scrape & Add
          </Button>
        ]}
        width={800}
      >
        {loadingDetails ? (
          <div className="flex justify-center items-center py-12">
            <Spin size="large" />
          </div>
        ) : eventDetails ? (
          <div className="space-y-4">
            {eventDetails.image && (
              <div className="mb-4">
                <img
                  src={eventDetails.image}
                  alt={eventDetails.name}
                  className="w-full max-h-64 object-cover rounded-lg"
                />
              </div>
            )}
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Title">{eventDetails.name}</Descriptions.Item>
              <Descriptions.Item label="Date/Time">{eventDetails.dateTime}</Descriptions.Item>
              <Descriptions.Item label="Venue">{eventDetails.location}</Descriptions.Item>
              <Descriptions.Item label="Address">{eventDetails.address}</Descriptions.Item>
              <Descriptions.Item label="Description">
                <div className="max-h-32 overflow-y-auto">
                  {eventDetails.description}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Lineup">
                {eventDetails.lineup?.join(', ') || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Genres">
                {eventDetails.genres?.map(g => (
                  <Tag key={g} color="blue">{g}</Tag>
                )) || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Promoter">{eventDetails.promoter || 'N/A'}</Descriptions.Item>
            </Descriptions>
          </div>
        ) : (
          <Alert message="No details available" type="info" />
        )}
      </Modal>
    </div>
  );
};

export default RAComparatorTab;
